/**
 * PaymentPolicy Component
 * 
 * Displays the payment and refund policies for YouthXConnect events.
 * Includes animations using framer-motion for enhanced user experience.
 */

import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

const PaymentPolicy = () => {
  return (
    <div>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Payment Policy - YouthXConnect</title>
        <meta
          name="description"
          content="YouthXConnect's Payment Policy and refund terms for events."
        />
        <meta
          name="keywords"
          content="Payment Policy, YouthXConnect, Refund Policy, Event Payments"
        />
        <meta property="og:title" content="Payment Policy - YouthXConnect" />
        <meta
          property="og:description"
          content="YouthXConnect's Payment Policy and refund terms for events."
        />
        <meta property="og:url" content="https://www.youthxconnect.com/payment-policy" />
      </Helmet>

      {/* Main Content Container */}
      <motion.div
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-20 max-w-4xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Page Title */}
        <motion.h1
          className="text-3xl sm:text-4xl font-bold mb-6 text-center text-gray-900"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          Payment Policy
        </motion.h1>

        {/* Payment Terms Section */}
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          Payment Terms
        </motion.h2>
        <motion.p 
          className="mb-4 text-base sm:text-lg text-gray-700" 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ delay: 0.5 }}
        >
          This Payment Policy ("Policy") constitutes a legally binding agreement between you and YouthXConnect. By making any payment or attempting to make any payment through our platform, you expressly acknowledge and agree to be bound by all terms herein.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          Payment Terms and Conditions
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
          All payments must be made in full at the time of registration. By initiating a payment, you warrant that:
          <ul className="list-disc pl-6 mt-2">
            <li>You have the legal right to use any payment method(s) you provide</li>
            <li>The information you provide is true, accurate, current, and complete</li>
            <li>You authorize us to charge your chosen payment method for the total amount</li>
            <li>You acknowledge that all payments are final and non-refundable except as explicitly stated in our Refund Policy</li>
            <li>You assume all risks associated with any payment method you choose to use</li>
          </ul>
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          Payment Processing Disclaimer
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
          YouthXConnect explicitly disclaims all liability for:
          <ul className="list-disc pl-6 mt-2">
            <li>Any payment processing errors or delays</li>
            <li>Any unauthorized transactions or fraudulent charges</li>
            <li>Any technical failures during payment processing</li>
            <li>Any currency conversion discrepancies or bank charges</li>
            <li>Any issues arising from payment gateway malfunctions</li>
            <li>Any double charges or incorrect amounts (resolution subject to payment processor's timeline)</li>
          </ul>
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 }}
        >
          Failed Transactions and Disputes
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>
          In the event of failed transactions or payment disputes:
          <ul className="list-disc pl-6 mt-2">
            <li>The Company bears no responsibility for failed transactions regardless of cause</li>
            <li>Users must directly contact their bank or payment provider for resolution</li>
            <li>The Company is not obligated to hold or reserve any bookings during dispute resolution</li>
            <li>Refunds for failed transactions may take up to 30 business days or longer</li>
            <li>The Company reserves the right to ban users who initiate fraudulent chargebacks</li>
            <li>All bank charges and processing fees are non-refundable under any circumstances</li>
          </ul>
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2 }}
        >
          Limitation of Liability
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, YOUTHXCONNECT AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO ANY PAYMENT TRANSACTIONS, EVEN IF YOUTHXCONNECT HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </motion.p>

        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.4 }}
        >
          Indemnification
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}>
          You agree to indemnify, defend, and hold harmless YouthXConnect and its affiliates from any claim, liability, cost, or expense arising from your violation of this Payment Policy or any applicable laws or regulations related to payment processing.
        </motion.p>

        {/* Accepted Payment Methods Section */}
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          Accepted Payment Methods
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
          We accept the following payment methods orignating from India:
        </motion.p>
        <motion.ul className="list-disc pl-6 mb-4 text-base sm:text-lg text-gray-700">
          <li>Debit Cards</li>
          <li>UPI Payments </li>
          <li>Net Banking</li>
        </motion.ul>

        {/* Transaction Security Section */}
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          Transaction Security
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
          All payments are processed through secure payment gateways that comply with PCI DSS standards. Your financial information is encrypted and protected using industry-standard security protocols.
        </motion.p>

        {/* Data Protection Section */}
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          Data Protection
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
          Your personal and payment information is handled with the utmost care and confidentiality. We do not share your data with third parties without your consent. Your data is stored in secure servers in India and is protected by industry-standard security measures.
        </motion.p>

        {/* Billing Process Section */}
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 }}
        >
          Billing Process
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>
          All amounts are in Indian Rupees (INR). You will receive a confirmation email with payment details after successful transaction completion. GST and other applicable taxes will be included in the final amount.
        </motion.p>

        {/* Legal and Contact Sections */}
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 }}
        >
          Legal Compliance
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>
          We may disclose your information where required by law or to protect our rights. This includes complying with local laws in India, responding to legal requests, and enforcing our terms of service.
        </motion.p>
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2 }}
        >
          Changes to This Policy
        </motion.h2>
        <motion.p className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>
          We may update our Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
        </motion.p>
        
        {/* Contact Information Section */}
        <motion.h2
          className="text-2xl sm:text-2xl font-semibold mt-6 mb-4 text-gray-800"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5 }}
        >
          Contact Information
        </motion.h2>
        <motion.div className="mb-4 text-base sm:text-lg text-gray-700" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}>
          <p>Email: <a href="mailto:connect@YouthXConnect.com" className="text-blue-500 underline">connect@YouthXConnect.com</a></p>
          <p>Phone: +91 88519 72135</p>
          <p>(Monday to Friday, 9:00 AM to 5:00 PM IST)</p>
        </motion.div>
      </motion.div>
      
    </div>
  );
};

export default PaymentPolicy;
